import useCurrentlyPlayingSong from "@hooks/useCurrentlyPlayingSong";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import SpotifyWebApi from "spotify-web-api-js";
import { useTokenContext } from "../auth/UserTokenProvider";
import { requestPlaybackSync } from "../music/playbackSync";

const spotifyApi = new SpotifyWebApi();

export interface QueuedTrack {
  uri: string;
  name: string;
}

interface AlbumContextState {
  isStickingToAlbum: boolean;
  setIsStickingToAlbum: (value: boolean) => void;
  stickedAlbumId: string | null;
  setStickedAlbumId: (albumId: string | null) => void;
  currentAlbumTracks: SpotifyApi.TrackObjectSimplified[] | null;
  pendingTracks: QueuedTrack[];
  lastQueuedTrack: QueuedTrack | null;
  queueError: string | null;
  stickToAlbum: (albumId: string) => Promise<boolean>;
  resumeOriginalPlaylist: () => Promise<boolean>;
  clearUpQueue: () => void;
}

const AlbumContext = createContext<AlbumContextState | undefined>(undefined);

export const useAlbumContext = () => {
  const context = useContext(AlbumContext);
  if (context === undefined) {
    throw new Error("useAlbumContext must be used within an AlbumProvider");
  }
  return context;
};

export const AlbumProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useTokenContext();
  const [isStickingToAlbum, setIsStickingToAlbum] = useState(false);
  const [stickedAlbumId, setStickedAlbumId] = useState<string | null>(null);
  const [currentAlbumTracks, setCurrentAlbumTracks] = useState<
    SpotifyApi.TrackObjectSimplified[] | null
  >(null);
  const [pendingTracks, setPendingTracks] = useState<QueuedTrack[]>([]);
  const [lastQueuedTrack, setLastQueuedTrack] = useState<QueuedTrack | null>(null);
  const [queueError, setQueueError] = useState<string | null>(null);

  const isQueuingInProgressRef = useRef(false);
  const lastProcessedTrackRef = useRef<string | null>(null);

  const currentlyPlayingSong = useCurrentlyPlayingSong();
  const currentTrackUri = currentlyPlayingSong?.currentlyPlayingObject?.item?.uri ?? null;

  useEffect(() => {
    if (token?.value) {
      spotifyApi.setAccessToken(token.value);
    }
  }, [token?.value]);

  const getCurrentPlaybackState = useCallback(async () => {
    try {
      return await spotifyApi.getMyCurrentPlaybackState();
    } catch (error) {
      console.error("Error getting playback state:", error);
      return null;
    }
  }, []);

  const clearUpQueue = useCallback(() => {
    setPendingTracks([]);
    setLastQueuedTrack(null);
    setQueueError(null);
    lastProcessedTrackRef.current = null;
    setIsStickingToAlbum(false);
    setStickedAlbumId(null);
  }, [setIsStickingToAlbum, setStickedAlbumId]);

  const resetStickAlbum = useCallback(() => {
    setIsStickingToAlbum(false);
    setStickedAlbumId(null);
    setCurrentAlbumTracks(null);
    setPendingTracks([]);
    setLastQueuedTrack(null);
    setQueueError(null);
    lastProcessedTrackRef.current = null;
  }, []);

  const tryQueueNextTrack = useCallback(
    (trackUri: string, pending: QueuedTrack[], lastQueued: QueuedTrack | null) => {
      if (pending.length === 0 || isQueuingInProgressRef.current) return;
      const expectedUri = lastQueued?.uri;
      const isOnExpectedTrack = !expectedUri || trackUri === expectedUri;
      if (!isOnExpectedTrack) return;
      const nextTrack = pending[0];
      isQueuingInProgressRef.current = true;
      setQueueError(null);
      spotifyApi
        .queue(nextTrack.uri)
        .then(() => {
          lastProcessedTrackRef.current = trackUri;
          setLastQueuedTrack(nextTrack);
          setPendingTracks(prev => prev.slice(1));
        })
        .catch(err => {
          console.error("Error queuing track to Spotify:", err);
          setQueueError(err instanceof Error ? err.message : "Failed to queue track to Spotify");
        })
        .finally(() => {
          isQueuingInProgressRef.current = false;
        });
    },
    []
  );

  // React to coordinated useCurrentlyPlayingSong updates.
  useEffect(() => {
    if (!currentTrackUri || !isStickingToAlbum || pendingTracks.length === 0) return;

    if (currentTrackUri === lastProcessedTrackRef.current) return;

    const expectedUri = lastQueuedTrack?.uri;
    const isOnExpectedTrack = !expectedUri || currentTrackUri === expectedUri;
    if (isOnExpectedTrack && !isQueuingInProgressRef.current) {
      tryQueueNextTrack(currentTrackUri, pendingTracks, lastQueuedTrack);
    } else {
      lastProcessedTrackRef.current = currentTrackUri;
    }
  }, [currentTrackUri, isStickingToAlbum, pendingTracks, lastQueuedTrack, tryQueueNextTrack]);

  // Retry queue operation when it fails
  useEffect(() => {
    if (!queueError || pendingTracks.length === 0 || !isStickingToAlbum || !lastQueuedTrack) return;
    if (currentTrackUri !== lastQueuedTrack.uri) return;
    if (isQueuingInProgressRef.current) return;

    const retryDelay = 8000;
    const timeoutId = setTimeout(async () => {
      const nextTrack = pendingTracks[0];
      setQueueError(null);
      isQueuingInProgressRef.current = true;
      try {
        console.log("Retrying queue:", nextTrack.name);
        await spotifyApi.queue(nextTrack.uri);
        lastProcessedTrackRef.current = currentTrackUri ?? null;
        setLastQueuedTrack(nextTrack);
        setPendingTracks(prev => prev.slice(1));
      } catch (err) {
        console.error("Retry queue failed:", err);
        setQueueError(err instanceof Error ? err.message : "Failed to queue track to Spotify");
      } finally {
        isQueuingInProgressRef.current = false;
      }
    }, retryDelay);

    return () => clearTimeout(timeoutId);
  }, [queueError, pendingTracks, lastQueuedTrack, currentTrackUri, isStickingToAlbum]);

  // Monitor album change - reset when user plays track from different album
  useEffect(() => {
    if (
      !isStickingToAlbum ||
      !stickedAlbumId ||
      !currentlyPlayingSong?.currentlyPlayingObject?.item
    )
      return;

    const currentTrack = currentlyPlayingSong.currentlyPlayingObject.item;
    const currentAlbumId = currentTrack.album.id;

    if (currentAlbumId !== stickedAlbumId) {
      console.log("Detected track from different album, stopping album queue");
      resetStickAlbum();
      return;
    }
  }, [isStickingToAlbum, stickedAlbumId, currentlyPlayingSong, resetStickAlbum]);

  const stickToAlbum = useCallback(
    async (albumId: string) => {
      try {
        setQueueError(null);
        lastProcessedTrackRef.current = null;

        const playback = await getCurrentPlaybackState();
        if (!playback?.item) return false;

        const albumTracks = await spotifyApi.getAlbumTracks(albumId);
        const currentTrackId = playback.item.id;
        const currentTrackIndex = albumTracks.items.findIndex(track => track.id === currentTrackId);

        const tracksToQueue = albumTracks.items.slice(currentTrackIndex + 1).map(track => ({
          uri: track.uri,
          name: track.name
        }));

        console.log(`Stored ${tracksToQueue.length} tracks in pending queue`);

        if (tracksToQueue.length > 0) {
          const nextTrack = tracksToQueue[0];
          await spotifyApi.queue(nextTrack.uri);
          setLastQueuedTrack(nextTrack);
          setPendingTracks(tracksToQueue.slice(1));
          lastProcessedTrackRef.current = playback.item.uri;
        } else {
          setLastQueuedTrack(null);
          setPendingTracks([]);
        }

        setIsStickingToAlbum(true);
        setStickedAlbumId(albumId);
        return true;
      } catch (error) {
        console.error("Error sticking to album:", error);
        setQueueError(error instanceof Error ? error.message : "Failed to stick to album");
        return false;
      }
    },
    [getCurrentPlaybackState, setStickedAlbumId, setIsStickingToAlbum]
  );

  const resumeOriginalPlaylist = useCallback(async () => {
    try {
      setPendingTracks([]);
      setLastQueuedTrack(null);
      setQueueError(null);
      lastProcessedTrackRef.current = null;
      setIsStickingToAlbum(false);
      setStickedAlbumId(null);

      await spotifyApi.skipToNext();
      await new Promise(resolve => setTimeout(resolve, 500));
      await spotifyApi.skipToNext();
      requestPlaybackSync();

      return true;
    } catch (error) {
      console.error("Error resuming original playlist:", error);
      return false;
    }
  }, [setIsStickingToAlbum, setStickedAlbumId]);

  const value: AlbumContextState = {
    isStickingToAlbum,
    setIsStickingToAlbum,
    stickedAlbumId,
    setStickedAlbumId,
    currentAlbumTracks,
    pendingTracks,
    lastQueuedTrack,
    queueError,
    stickToAlbum,
    resumeOriginalPlaylist,
    clearUpQueue
  };

  return <AlbumContext.Provider value={value}>{children}</AlbumContext.Provider>;
};
